import { Alert, Box, Button, Snackbar } from '@mui/material';
import { IconComponent } from '@zippeditoolsjs/zippedi-icons';
import React, { useEffect, useRef, useState } from 'react';

const NotSeenAchievementsSnack = ({
    open,
    onClose,
    onViewAchievements,
    achievementsCount = 0,
    autoHideDuration = 2500
}) => {
    const [isPaused, setIsPaused] = useState(false);
    const animationRef = useRef(null);
    const startTimeRef = useRef(null);


    const handleClose = (event, reason) => {
        if (reason === 'clickaway') {
            return;
        }
        onClose();
    };

    const handleViewAchievements = () => {
        onViewAchievements();
        onClose();
    };

    const handleMouseEnter = () => {
        setIsPaused(true);
    };

    const handleMouseLeave = () => {
        setIsPaused(false);
    };

    // Progress bar countdown effect
    useEffect(() => {
        if (!open) {
            setIsPaused(false);
            if (animationRef.current) {
                cancelAnimationFrame(animationRef.current);
                animationRef.current = null;
            }
            return;
        }

        // Reset progress when snack opens
        setIsPaused(false);
        startTimeRef.current = Date.now();
        const endTime = startTimeRef.current + autoHideDuration;

        const updateProgress = () => {
            if (isPaused) {
                animationRef.current = requestAnimationFrame(updateProgress);
                return;
            }

            const currentTime = Date.now();
            const remainingTime = Math.max(0, endTime - currentTime);

            if (remainingTime > 0 && open) {
                animationRef.current = requestAnimationFrame(updateProgress);
            }
        };

        animationRef.current = requestAnimationFrame(updateProgress);

        return () => {
            if (animationRef.current) {
                cancelAnimationFrame(animationRef.current);
                animationRef.current = null;
            }
        };
    }, [open, autoHideDuration, isPaused]);

    // Handle pause state changes
    useEffect(() => {
        if (!open) return;

        if (isPaused) {
            // Pause the animation
            if (animationRef.current) {
                cancelAnimationFrame(animationRef.current);
                animationRef.current = null;
            }
        } else {
            // Resume the animation
            if (startTimeRef.current) {
                const endTime = startTimeRef.current + autoHideDuration;

                const updateProgress = () => {
                    const currentTime = Date.now();
                    const remainingTime = Math.max(0, endTime - currentTime);

                    if (remainingTime > 0 && open && !isPaused) {
                        animationRef.current = requestAnimationFrame(updateProgress);
                    }
                };

                animationRef.current = requestAnimationFrame(updateProgress);
            }
        }
    }, [isPaused, open, autoHideDuration]);

    return (
        <Snackbar
            open={open}
            autoHideDuration={autoHideDuration}
            onClose={handleClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
            <Box
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
                sx={{ width: '100%' }}
            >
                <Alert
                    onClose={handleClose}
                    severity="info"
                    variant="filled"
                    sx={{
                        width: '100%',
                        backgroundColor: '#1976d2',
                        '& .MuiAlert-icon': {
                            color: 'white'
                        },
                        '& .MuiAlert-message': {
                            color: 'white'
                        }
                    }}
                    action={
                        <Box display="flex" alignItems="center" gap={1}>
                            <Button
                                color="inherit"
                                size="small"
                                onClick={handleViewAchievements}
                                startIcon={<IconComponent iconName="trophy" style={{ color: 'white' }} />}
                                sx={{
                                    color: 'white',
                                    borderColor: 'white',
                                    '&:hover': {
                                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                        borderColor: 'white'
                                    }
                                }}
                                variant="outlined"
                            >
                                Ver ({achievementsCount})
                            </Button>
                        </Box>
                    }
                >
                    ¡Tienes {achievementsCount} nuevo{achievementsCount !== 1 ? 's' : ''} logro{achievementsCount !== 1 ? 's' : ''} sin ver!
                </Alert>
            </Box>
        </Snackbar>
    );
};

export default NotSeenAchievementsSnack; 